import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MailCheck, Loader2, ShieldAlert, FileSignature, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";

/**
 * Jul-2026 (User-Wunsch) — kompakter Sammel-Banner für neu registrierte Händler:
 * Konto-Freigabe, E-Mail-Bestätigung, AVV und Händler-Nutzungsbedingungen
 * (SaaS-Lizenz) in EINER Checkliste. AVV/Nutzungsbedingungen erscheinen auch,
 * wenn eine neue Version vorliegt und erneut bestätigt werden muss
 * (Backend vergleicht die akzeptierte Version).
 */
export default function DealerApprovalBanner() {
    const { user } = useAuth();
    const [sending, setSending] = useState(false);
    const [sentLink, setSentLink] = useState("");
    const [legal, setLegal] = useState({ avv: true, terms: true });

    const isDealer = user?.role === "b2b";
    useEffect(() => {
        if (!isDealer) return;
        let alive = true;
        Promise.all([
            api.get("/privacy/avv").then((r) => r.data.accepted).catch(() => true),
            api.get("/privacy/dealer-terms").then((r) => r.data.accepted).catch(() => true),
        ]).then(([avv, terms]) => { if (alive) setLegal({ avv, terms }); });
        return () => { alive = false; };
    }, [isDealer]);

    if (!isDealer) return null;
    const pending = user.verification_status === "pending";
    const needsEmail = user.email_verified !== true;
    const needsAvv = !legal.avv;
    const needsTerms = !legal.terms;
    if (!pending && !needsEmail && !needsAvv && !needsTerms) return null;

    const resend = async () => {
        setSending(true);
        try {
            const { data } = await api.post("/auth/resend-verification");
            if (data?.verify_url) setSentLink(data.verify_url);
            if (data?.sent === false && !data?.verify_url) {
                toast.error("E-Mail-Versand derzeit nicht möglich. Unser Team wurde informiert — bitte später erneut versuchen.");
            } else {
                toast.success("Bestätigungsmail wurde erneut versendet.");
            }
        } catch {
            toast.error("Senden fehlgeschlagen.");
        } finally {
            setSending(false);
        }
    };

    const Row = ({ icon: Icon, children, action, testid }) => (
        <li className="flex items-center gap-2 py-1.5 border-t border-amber-200/70 first:border-t-0" data-testid={testid}>
            <Icon className="w-4 h-4 text-amber-700 shrink-0" />
            <span className="text-xs text-amber-900 flex-1 min-w-0">{children}</span>
            {action}
        </li>
    );

    return (
        <div className="mb-6 bg-amber-50 border-2 border-amber-300 rounded-sm px-4 py-3" data-testid="dealer-approval-banner">
            <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-700 shrink-0" />
                <h3 className="font-display font-black text-sm text-amber-900">
                    {pending ? "Ihr Konto wird geprüft" : "Noch zu erledigen"}
                </h3>
                {pending && (
                    <span className="text-[11px] text-amber-900/70 hidden sm:inline truncate">
                        — Freigabe folgt zeitnah. Profil &amp; Stammdaten können Sie schon pflegen; Veröffentlichen/Verkaufen erst nach Freigabe.
                    </span>
                )}
            </div>
            {pending && (
                <p className="text-[11px] text-amber-900/70 mt-1 sm:hidden">
                    Freigabe folgt zeitnah. Profil &amp; Stammdaten können Sie schon pflegen; Veröffentlichen/Verkaufen erst nach Freigabe.
                </p>
            )}
            <ul className="mt-2">
                {needsEmail && (
                    <Row icon={MailCheck} testid="banner-row-email" action={
                        <button type="button" onClick={resend} disabled={sending}
                            className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider bg-amber-700 hover:bg-amber-800 text-white px-2.5 py-1 rounded-sm disabled:opacity-50 shrink-0"
                            data-testid="dealer-resend-verification-btn">
                            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <MailCheck className="w-3 h-3" />}
                            Erneut senden
                        </button>
                    }>
                        <strong>E-Mail-Adresse bestätigen</strong> — Link im Postfach prüfen
                    </Row>
                )}
                {needsAvv && (
                    <Row icon={ShieldAlert} testid="banner-row-avv" action={
                        <Link to="/legal/avv" className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-amber-700 hover:bg-amber-800 text-white px-2.5 py-1 rounded-sm shrink-0" data-testid="banner-cta-avv">
                            Prüfen &amp; akzeptieren
                        </Link>
                    }>
                        <strong>Auftragsverarbeitungsvertrag (AVV)</strong> akzeptieren bzw. neu bestätigen
                    </Row>
                )}
                {needsTerms && (
                    <Row icon={FileSignature} testid="banner-row-terms" action={
                        <Link to="/legal/haendler-nutzungsbedingungen" className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-amber-700 hover:bg-amber-800 text-white px-2.5 py-1 rounded-sm shrink-0" data-testid="banner-cta-terms">
                            Prüfen &amp; akzeptieren
                        </Link>
                    }>
                        <strong>Händler-Nutzungsbedingungen (SaaS-Lizenz)</strong> akzeptieren bzw. neu bestätigen
                    </Row>
                )}
                {!needsEmail && !needsAvv && !needsTerms && (
                    <Row icon={CheckCircle2} testid="banner-row-allok">Alle Bestätigungen liegen vor — es fehlt nur noch die Freigabe durch unser Team.</Row>
                )}
            </ul>
            {sentLink && (
                <p className="mt-1.5 text-[10px] text-gray-500 break-all" data-testid="dealer-verify-dev-link">
                    Dev-Link: <a href={sentLink} className="underline font-mono-num">{sentLink}</a>
                </p>
            )}
        </div>
    );
}
